import React, { useState, useEffect } from "react";
import { X, Loader2, PlayCircle } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

export default function DeploymentRunFormDialog({ tenants = [], open, onClose, onCreated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      tenant_id: tenants[0]?.tenant_id || "",
      deployment_id: "",
      provision_id: "",
      environment_config_id: "",
      configuration_id: "",
      notes: "",
    });
  }, [open, tenants]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.tenant_id) { toast.error("Pilih tenant dulu."); return; }
    setBusy(true);
    try {
      const payload = { tenant_id: form.tenant_id };
      if (form.deployment_id) payload.deployment_id = form.deployment_id;
      if (form.provision_id) payload.provision_id = form.provision_id;
      if (form.environment_config_id) payload.environment_config_id = form.environment_config_id;
      if (form.configuration_id) payload.configuration_id = form.configuration_id;
      if (form.notes) payload.notes = form.notes;
      const r = await api.post("/platform/deployment-runner", payload);
      toast.success(`Run ${r.data.run.run_number} dibuat.`);
      onCreated?.(r.data.run);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal membuat deployment run"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="run-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">Buat Deployment Run</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Tenant *</label>
            <Select value={form.tenant_id} onValueChange={(v) => set({ tenant_id: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="run-form-tenant"><SelectValue placeholder="Pilih tenant…" /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id} className={ITEM_CLS}>{t.tenant_name} · {t.tenant_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-white/40 mt-1">Runner akan otomatis pakai environment config <span className="text-emerald-300">ACTIVE</span> tenant (PRODUCTION) bila tidak ada override.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Deployment ID</label>
              <Input value={form.deployment_id} onChange={(e) => set({ deployment_id: e.target.value })} placeholder="opsional" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="run-form-deployment-id" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Provision ID</label>
              <Input value={form.provision_id} onChange={(e) => set({ provision_id: e.target.value })} placeholder="opsional" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="run-form-provision-id" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Env Config ID</label>
              <Input value={form.environment_config_id} onChange={(e) => set({ environment_config_id: e.target.value })} placeholder="opsional" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="run-form-env-id" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Config ID</label>
              <Input value={form.configuration_id} onChange={(e) => set({ configuration_id: e.target.value })} placeholder="opsional" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="run-form-config-id" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Notes (opsional)</label>
            <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Catatan run…" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="run-form-notes" />
          </div>

          <div className="text-[10px] text-white/40 leading-relaxed">
            Deployment Runner ini foundation only — menghasilkan <strong className="text-white/70">artifact bundle (docker-compose.yml, backend.env, frontend.env, nginx.conf, healthcheck.json + execution report)</strong>. TIDAK mengeksekusi SSH/Docker/VPS/DNS.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.tenant_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="run-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Membuat…</> : <><PlayCircle size={14} className="mr-1" /> Buat</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
